import { z } from 'zod';
import { GalleryItemSchema, type GalleryItem } from './salonSettings';

// Хранилище фото галереи.
// В production файлы лежат среди публичных ассетов Pages, а URL пишется
// в business.json / tenants.business. Здесь — единая точка,
// чтобы кабинет владельца и витрина работали одинаково.
// Демо: фото хранятся как data-URL в localStorage (без сети и без бакета).

const KEY = 'mnc-gallery-uploads';

const UploadListSchema = z.array(GalleryItemSchema);

// Список загруженных владельцем фото (в порядке добавления).
export function readGalleryUploads(): GalleryItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = UploadListSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

function writeGalleryUploads(list: GalleryItem[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* приватный режим — фото живёт только на текущей странице */
  }
}

// Прочитать файл как data-URL для автономного demo-режима.
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
    reader.readAsDataURL(file);
  });
}

// Добавить фото. Ограничение 4 МБ — data-URL в localStorage не резиновый.
export const MAX_PHOTO_BYTES = 4 * 1024 * 1024;

export async function addGalleryPhoto(file: File, caption: string): Promise<GalleryItem> {
  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error('Фото больше 4 МБ — сожмите его перед загрузкой.');
  }
  const src = await fileToDataUrl(file);
  const item: GalleryItem = { src, caption: caption.trim() || 'Работа' };
  const list = readGalleryUploads();
  list.push(item);
  writeGalleryUploads(list);
  return item;
}

export function removeGalleryPhoto(src: string): void {
  writeGalleryUploads(readGalleryUploads().filter((g) => g.src !== src));
}
